import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import styled from '@emotion/styled'

import {Button} from '@vio/atlas/Button'
import {InputTextType, MD_INPUT_HEIGHT} from '@vio/atlas/helpers/InputBase'
import {InputButtonStyledContainer} from '@vio/atlas/helpers/InputButton'
import {Icon} from '@vio/atlas/Icon'
import {InputText} from '@vio/atlas/InputText'
import {cssTheme} from '@vio/atlas/themes'
import useDebounce from '@vio/core/src/utils/debounce/hooks/useDebounce'

import {destinationPickerMessages} from './SearchFormHorizontal'
import {DIVIDER_WIDTH, IconWrapper} from './styles'

interface Props {
  hasError?: boolean
  /** Destination name */
  destination: string
  /** Callback for when autocomplete data is requested */
  onSuggestionsRequested: (
    value: string,
    shouldLoadUserSearchHistory: boolean
  ) => void
  /** Whether search history should be shown */
  shouldLoadUserSearchHistory?: boolean
  /** Whether the input should be focused on mount */
  focusOnMount?: boolean
}

const DestinationInput = styled(InputText)`
  label.--is-invalid {
    border-color: ${cssTheme.colors.input.error.border};
    outline-color: ${cssTheme.colors.input.error.border};
  }

  position: absolute;
  top: 0;
  height: ${MD_INPUT_HEIGHT}px;
  left: -${DIVIDER_WIDTH}px;
  width: calc(100% + ${DIVIDER_WIDTH * 2}px);
  padding: 0 ${DIVIDER_WIDTH * 2}px !important;
  label {
    padding: 0 10px;
    border-radius: ${cssTheme.layout.radius.rounded};
  }
  ${IconWrapper} {
    margin-left: -1px;
    padding-right: 1px;
  }
  ${InputButtonStyledContainer} {
    background: initial;
  }
  &.--is-first-input {
    border-top-left-radius: ${cssTheme.layout.radius.rounded};
    border-bottom-left-radius: ${cssTheme.layout.radius.rounded};
  }
  input {
    background: white !important;
    ${cssTheme.typography.bodyS};
    &::-webkit-search-cancel-button {
      display: none;
    }
  }
`

export const SearchFormDestinationInput = React.memo(
  function SearchFormDestinationInput({
    destination,
    hasError = false,
    onSuggestionsRequested,
    shouldLoadUserSearchHistory = false,
    focusOnMount = false
  }: Props) {
    const {formatMessage} = useIntl()
    const inputFieldRef = useRef<null | HTMLInputElement>(null)
    const [inputValue, setInputValue] = useState(destination)
    const debouncedInputValue = useDebounce(inputValue, 250)

    useEffect(() => {
      if (focusOnMount) inputFieldRef.current?.focus?.()
    }, [focusOnMount])

    useEffect(() => {
      setInputValue(destination)
    }, [setInputValue, destination])

    // Requesting suggestions can cause a loop when we rely on dep stability so check that value is actually changed
    const previousDebouncedInputValue = useRef<string>(debouncedInputValue)

    useEffect(() => {
      const isChanged =
        debouncedInputValue !== previousDebouncedInputValue.current
      if (!isChanged) return

      previousDebouncedInputValue.current = debouncedInputValue

      onSuggestionsRequested(
        debouncedInputValue ?? '',
        Boolean(!debouncedInputValue)
      )
    }, [debouncedInputValue, onSuggestionsRequested])

    const handleInputChange = useCallback(
      (value: string) => {
        setInputValue(value)
      },
      [setInputValue]
    )

    const handleInputFocus = useCallback(
      ({target}: React.FocusEvent<HTMLInputElement>) => {
        const {value} = target

        if (destination === '') {
          onSuggestionsRequested('', true)
        } else {
          onSuggestionsRequested(value, shouldLoadUserSearchHistory)
        }
      },
      [destination, shouldLoadUserSearchHistory, onSuggestionsRequested]
    )

    const handleClearDestination = useCallback(
      (event: React.SyntheticEvent) => {
        event.preventDefault()

        // Refocus first so the focus handler cannot overwrite the cleared value.
        inputFieldRef.current?.focus()
        setInputValue('')
      },
      [inputFieldRef, setInputValue]
    )

    return (
      <DestinationInput
        ref={inputFieldRef}
        isInvalid={hasError}
        customProps={{
          role: 'presentation',
          'aria-invalid': hasError || undefined,
          autoCapitalize: 'off',
          autoCorrect: 'off',
          spellCheck: 'false',
          autocomplete: 'off',
          dataLpignore: true,
          dataFormType: 'other'
        }}
        id="DestinationInput"
        dataId="DestinationInput"
        name="DestinationInput"
        onFocus={handleInputFocus}
        prefix={
          <IconWrapper>
            <Icon name="Pin" />
          </IconWrapper>
        }
        placeholder={formatMessage(
          destinationPickerMessages.destinationPlaceholder
        )}
        type={InputTextType.search}
        value={inputValue}
        onChange={handleInputChange}
        suffix={
          inputValue && (
            <Button
              onClick={handleClearDestination}
              variant="ghost"
              size="sm"
              dataId="DestinationInputClear"
              iconStart={
                <Icon
                  name="Clear"
                  size="sm"
                  colorPath="content.neutral.secondary"
                />
              }
            />
          )
        }
      />
    )
  }
)
